import { z } from 'zod';

import { AuthContextSchema } from './auth.js';
import {
  CategoryIdSchema,
  ContractDocumentSchema,
  ContractStatusSchema,
  ContractTypeSchema,
} from './contract-document.js';
import { CostRatePeriodSchema, CustomerRefSchema } from './external-refs.js';
import { ProjectDocumentSchema, ProjectDocumentShapeSchema } from './project-document.js';

/**
 * The HTTP contract. The API registers these as Fastify route schemas (so
 * validation, serialisation and the OpenAPI document all derive from them) and
 * the web app infers its client types from the same objects.
 *
 * Responses carrying derived numbers (summary) mirror the output types of
 * calculations.ts — computed on load, never stored.
 */

// ─── System ───

export const HealthResponseSchema = z.object({
  status: z.enum(['healthy', 'degraded']),
  database: z.enum(['up', 'down']),
  uptimeSeconds: z.number(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/** How TimeLog lookups are served — see the Lookups section below. */
export const LookupModeSchema = z.enum(['off', 'fixture', 'live']);
export type LookupMode = z.infer<typeof LookupModeSchema>;

/**
 * Where an employee's default role comes from (D21): `stand-in` is the
 * prototype's stable pseudo-random assignment from the caller's role list.
 */
export const EmployeeRoleSourceSchema = z.enum(['off', 'stand-in']);
export type EmployeeRoleSource = z.infer<typeof EmployeeRoleSourceSchema>;

/**
 * Where cost prices come from (ADR 0042). `stand-in`: every role and every
 * employee is handed one of the caller's legal entity's standard cost prices
 * (`/v1/cost-price`) by a stable hash — a PROTOTYPE stand-in, since TimeLog
 * exposes neither an employee's nor a role's actual cost through any API PM
 * can reach. Reported on /api/me so the UI can say what the numbers are.
 */
export const CostPriceSourceSchema = z.enum(['off', 'stand-in']);
export type CostPriceSource = z.infer<typeof CostPriceSourceSchema>;

export const MeResponseSchema = z.object({
  auth: AuthContextSchema,
  authMode: z.enum(['oidc', 'test-login']),
  /** Feature availability, so the UI can degrade (e.g. hide pickers) without probing. */
  features: z.object({
    lookups: LookupModeSchema,
    /**
     * Read-only TimeLog reference data (ADR 0035): the hourly-rate catalog at
     * GET /api/tlp/rates. Non-off ⇒ the catalog is reachable.
     */
    timelog: LookupModeSchema,
    /** Whether the person dropdown's roles are real data or the prototype stand-in (D21). */
    employeeRoles: EmployeeRoleSourceSchema,
    /** Whether roles and employees carry a cost price, and from where (ADR 0042). */
    costPrices: CostPriceSourceSchema,
  }),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const ErrorResponseSchema = z.object({
  statusCode: z.number(),
  error: z.string(),
  message: z.string(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// ─── Projects ───

export const ProjectStatusSchema = z.enum(['draft', 'active', 'archived']);
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

/**
 * List rows come from the relational columns only — no documents are read.
 *
 * `customerName` is the one exception, and deliberately a narrow one: the
 * portfolio table needs a customer per row, but the customer lives inside the
 * plan document (`document.customer.snapshot.name`). The repository reads it
 * with a jsonb path expression, so the row is still one indexed read and the
 * document itself never leaves Postgres. It is the denormalised NAME, not the
 * `CustomerRef` — a list row has no business carrying `tlpId`/`capturedAt`.
 */
export const ProjectListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** `document.customer.snapshot.name`, or null when the project has no customer. */
  customerName: z.string().nullable(),
  status: ProjectStatusSchema,
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectListItem = z.infer<typeof ProjectListItemSchema>;

export const ProjectListResponseSchema = z.object({
  projects: z.array(ProjectListItemSchema),
});
export type ProjectListResponse = z.infer<typeof ProjectListResponseSchema>;

/**
 * Responses use the descriptive shape schema so OpenAPI shows the full recursive
 * PlanNode tree. Serialization on document-bearing routes is passthrough
 * (JSON.stringify of trusted server state) — response documents are never
 * re-validated, which matters at 10,000-task scale.
 */
export const ProjectResponseSchema = ProjectListItemSchema.extend({
  document: ProjectDocumentShapeSchema,
});
export type ProjectResponse = z.infer<typeof ProjectResponseSchema>;

export const CreateProjectRequestSchema = z.object({
  name: z.string().min(1).max(200),
  code: z.string().max(50).optional(),
  description: z.string().max(4000).optional(),
  customer: CustomerRefSchema.optional(),
  currency: z.string().length(3).optional(),
  blendedCostRate: z.number().min(1).max(100_000).optional(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

export const UpdateProjectRequestSchema = z.object({
  document: ProjectDocumentSchema,
  status: ProjectStatusSchema.optional(),
  /** Optimistic concurrency: the version the client last read (BR-20). */
  version: z.number().int().min(1),
});
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;

export const ProjectIdParamsSchema = z.object({ id: z.string().uuid() });
export type ProjectIdParams = z.infer<typeof ProjectIdParamsSchema>;

// ─── Contracts ───

export const ContractResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  type: ContractTypeSchema,
  status: ContractStatusSchema,
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  document: ContractDocumentSchema,
});
export type ContractResponse = z.infer<typeof ContractResponseSchema>;

export const ContractListResponseSchema = z.object({
  contracts: z.array(ContractResponseSchema),
});
export type ContractListResponse = z.infer<typeof ContractListResponseSchema>;

/** A section's seeded uplift, bounded like the flag payload's `uplift` default. */
const CategoryFactorSchema = z.number().min(0.1).max(20);

/**
 * Create-time seed from the budget-model config (BR-27, CALC-25). 'own' can
 * never hide, but it does carry a factor — `categoryFactors` spans all five
 * sections. The API applies whatever the client sends and reads no flag itself.
 */
export const ContractSeedSchema = z.object({
  hiddenCategories: z.array(z.enum(['cont', 'exp', 'sub', 'mat'])).optional(),
  targetFactor: z.number().min(0.1).max(100).optional(),
  // Spelled out rather than `z.record(CategoryIdSchema, …)`: a record over an
  // enum key is exhaustive in Zod 4, which would refuse a caller that knows
  // only some sections — and a partial map is what shared's
  // `createEmptyContractDocument` documents (the rest fall back to CATEGORY_DEFS).
  categoryFactors: z
    .object({
      own: CategoryFactorSchema,
      cont: CategoryFactorSchema,
      exp: CategoryFactorSchema,
      sub: CategoryFactorSchema,
      mat: CategoryFactorSchema,
    })
    .partial()
    .optional(),
});
export type ContractSeed = z.infer<typeof ContractSeedSchema>;

export const CreateContractRequestSchema = z.object({
  name: z.string().min(1).max(200),
  type: ContractTypeSchema,
  currency: z.string().length(3).optional(),
  seed: ContractSeedSchema.optional(),
});
export type CreateContractRequest = z.infer<typeof CreateContractRequestSchema>;

export const UpdateContractRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  type: ContractTypeSchema.optional(),
  status: ContractStatusSchema.optional(),
  document: ContractDocumentSchema,
  version: z.number().int().min(1),
});
export type UpdateContractRequest = z.infer<typeof UpdateContractRequestSchema>;

export const ContractIdParamsSchema = z.object({
  id: z.string().uuid(),
  cid: z.string().uuid(),
});
export type ContractIdParams = z.infer<typeof ContractIdParamsSchema>;

/** BR-7: deleting a referenced contract requires a reassignment target. */
export const DeleteContractQuerySchema = z.object({
  reassignTo: z.string().uuid().optional(),
});
export type DeleteContractQuery = z.infer<typeof DeleteContractQuerySchema>;

/**
 * BR-10: bill a category via a sibling contract — one transactional write over
 * both contracts (BR-20). `out` marks the category `billedVia` on the source
 * and adds the cost-first line on the target (CALC-14a); `back` reverses both
 * sides. Both versions are checked; either being stale is a 409.
 */
export const MoveCategoryRequestSchema = z.object({
  categoryId: z.enum(['exp', 'sub', 'mat']),
  targetContractId: z.string().uuid(),
  direction: z.enum(['out', 'back']),
  /** Version of the source contract (`:cid`) the client last read. */
  version: z.number().int().min(1),
  /** Version of the target contract the client last read. */
  targetVersion: z.number().int().min(1),
});
export type MoveCategoryRequest = z.infer<typeof MoveCategoryRequestSchema>;

export const MoveCategoryResponseSchema = z.object({
  source: ContractResponseSchema,
  target: ContractResponseSchema,
});
export type MoveCategoryResponse = z.infer<typeof MoveCategoryResponseSchema>;

// ─── Summary: the derived read model (everything computed on load) ───

const NodeRollupSchema = z.object({
  hours: z.number(),
  amount: z.number(),
  realized: z.number(),
  realizedPct: z.number(),
  shareOfPlanPct: z.number(),
});

const PlanSummarySchema = z.object({
  totals: z.object({
    hours: z.number(),
    amount: z.number(),
    realized: z.number(),
    realizedPct: z.number(),
  }),
  perDeliverable: z.array(
    z.object({
      id: z.string(),
      code: z.string().optional(),
      name: z.string(),
      hours: z.number(),
      amount: z.number(),
      sharePct: z.number(),
    }),
  ),
  /** CALC-3 rollup per plan-node id — one entry for every node in the tree. */
  nodes: z.record(z.string(), NodeRollupSchema),
  unassignedHours: z.number(),
  warnings: z.array(z.string()),
});

const CategoryFinancialsSchema = z.object({
  id: CategoryIdSchema,
  amount: z.number(),
  targetFactor: z.number(),
  cost: z.number(),
  marginPct: z.number(),
  sharePct: z.number(),
  hidden: z.boolean(),
  /** CALC-19 — always true on the summary endpoint (it is config-agnostic). */
  available: z.boolean(),
  billedVia: z.string().uuid().optional(),
});

const ContractFinancialsSchema = z.object({
  revenue: z.number(),
  cost: z.number(),
  budgetedFactor: z.number().nullable(),
  marginPct: z.number().nullable(),
  notAllocated: z.number(),
  overDistributed: z.boolean(),
  ownHours: z.number(),
  categories: z.array(CategoryFinancialsSchema),
});

const ContractConsumptionSchema = z.object({
  hoursBudget: z.number(),
  revenueBudget: z.number(),
  ownRevenueBudget: z.number(),
  ownCostBudget: z.number(),
  consumedHours: z.number(),
  consumedAmount: z.number(),
  consumedCost: z.number(),
  remainingHours: z.number(),
  remainingAmount: z.number(),
});

const ProjectFinancialsSchema = z.object({
  contractCount: z.number().int(),
  revenue: z.number(),
  cost: z.number(),
  budgetedFactor: z.number().nullable(),
  marginPct: z.number().nullable(),
  ownHours: z.number(),
  ownRevenue: z.number(),
  avgRate: z.number().nullable(),
  forecastedFactor: z.number().nullable(),
});

export const ProjectSummaryResponseSchema = z.object({
  projectId: z.string().uuid(),
  name: z.string(),
  version: z.number().int(),
  currency: z.string(),
  plan: PlanSummarySchema,
  contracts: z.array(
    z.object({
      contractId: z.string().uuid(),
      name: z.string(),
      type: ContractTypeSchema,
      currency: z.string(),
      financials: ContractFinancialsSchema,
      consumption: ContractConsumptionSchema,
    }),
  ),
  project: ProjectFinancialsSchema,
});
export type ProjectSummaryResponse = z.infer<typeof ProjectSummaryResponseSchema>;

// ─── Lookups: TimeLog external data, proxied server-side ───
//
// The API proxies Typesense (employees, customers) and the TimeLog REST API
// (roles) so the browser never holds cluster credentials and the tenant filter
// always comes from the verified JWT. See docs/adr on TimeLog lookups.
// LookupModeSchema lives next to MeResponseSchema, which embeds it.

export const LookupQuerySchema = z.object({
  /** Empty string browses (Typesense `q=*`). */
  q: z.string().max(200).default(''),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type LookupQuery = z.infer<typeof LookupQuerySchema>;

/**
 * One employee hit. `position`/`roleName`/`departmentName` are the employee's
 * OWN dimensions (job title, TimeLog RBAC role, department) — context only,
 * never a role in this module's sense. The role fields exist only where a
 * source supplies them — the fixture does; live TimeLog exposes neither
 * through any API PM can reach (verified 2026-09-15), so in live mode they
 * come from the prototype stand-in when `EMPLOYEE_ROLE_SOURCE=stand-in` (D21):
 * - `defaultRoleServiceId` / `defaultRoleName` — the employee's standard hourly
 *   rate as a ServiceID and its name (D1): what the person dropdown shows
 *   beside the name and what a person-first pick defaults the line to (D5, D16);
 * - `costRates` — the time-phased cost schedule, snapshotted onto the
 *   `PersonRef` at pick time and resolved on the evaluation day (D14). In live
 *   mode it is the prototype stand-in when `COST_PRICE_SOURCE=stand-in`
 *   (ADR 0042): one open-ended period at one of the entity's standard cost
 *   prices, picked by a stable hash of the employee id.
 * Absent = unknown: the role stays for the user to pick, cost falls through.
 */
export const EmployeeLookupItemSchema = z.object({
  tlpId: z.string().min(1),
  name: z.string().min(1),
  initials: z.string().optional(),
  email: z.string().optional(),
  position: z.string().optional(),
  roleName: z.string().optional(),
  departmentName: z.string().optional(),
  isProjectManager: z.boolean().optional(),
  defaultRoleServiceId: z.string().min(1).optional(),
  defaultRoleName: z.string().min(1).optional(),
  costRates: z.array(CostRatePeriodSchema).optional(),
});
export type EmployeeLookupItem = z.infer<typeof EmployeeLookupItemSchema>;

export const EmployeeLookupResponseSchema = z.object({
  items: z.array(EmployeeLookupItemSchema),
  /** Server time — the UI stamps `capturedAt` on refs built from these items. */
  capturedAt: z.string(),
});
export type EmployeeLookupResponse = z.infer<typeof EmployeeLookupResponseSchema>;

export const CustomerLookupItemSchema = z.object({
  tlpId: z.string().min(1),
  name: z.string().min(1),
  customerNo: z.string().optional(),
  city: z.string().optional(),
  vatNo: z.string().optional(),
});
export type CustomerLookupItem = z.infer<typeof CustomerLookupItemSchema>;

export const CustomerLookupResponseSchema = z.object({
  items: z.array(CustomerLookupItemSchema),
  capturedAt: z.string(),
});
export type CustomerLookupResponse = z.infer<typeof CustomerLookupResponseSchema>;

export const SupplierLookupItemSchema = z.object({
  tlpId: z.string().min(1),
  name: z.string().min(1),
  discipline: z.string().optional(),
});
export type SupplierLookupItem = z.infer<typeof SupplierLookupItemSchema>;

export const SupplierLookupResponseSchema = z.object({
  items: z.array(SupplierLookupItemSchema),
  capturedAt: z.string(),
});
export type SupplierLookupResponse = z.infer<typeof SupplierLookupResponseSchema>;
